'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { SocialIcons } from '@/elements/social-icons/social-icons.component';
import { NewsletterForm } from '@/elements/newsletter-form/newsletter-form.component';
import { ContactForm } from '@/elements/contact-form/contact-form.component';
import { PaymentCard } from '@/elements/payment-card/payment-card.component';
import { WeBuiltThis } from '@/elements/we-built-this/we-built-this.component';
import { Icon } from '@/elements/icon/icon.component';
import {
  Container,
  InnerContainer,
  Section,
  ColumnWrap,
  Column,
  Title,
  Subtitle,
  TitleWrap,
  Links,
  LinksColumn,
  Copyright,
  SocialIconsWrap,
  ContactMediums,
} from './footer.elements';

import { IconPhoneIncoming, IconSend } from '@tabler/icons-react';

import { companyInfo } from '@/data/company-info.data';

// Obfuscated contact info to protect from bots
const obfuscatedEmail = btoa(companyInfo.email);
const obfuscatedPhone = btoa(companyInfo.phoneNumber);

const Footer = ({ pages }) => {
  const pathname = usePathname();
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '' });

  // Decode contact info after mount to protect from bots
  useEffect(() => {
    setContactInfo({
      email: atob(obfuscatedEmail),
      phone: atob(obfuscatedPhone),
    });
  }, []);

  return (
    <Container>
      <InnerContainer>
        <SocialIconsWrap>
          <SocialIcons />
        </SocialIconsWrap>
        <Section>
          {pathname === '/' ? (
            <ColumnWrap>
              <Column>
                <Title>Prijavi se na newsletter</Title>
                <Subtitle>
                  Prijavom na newsletter, besplatno dobivaš korisne savjete za osobni razvoj i
                  informacije o nadolazećim edukacijama.
                </Subtitle>
                <br />
                <Subtitle $darker>
                  Sve informacije vezane uz zaštitu osobnih podataka možeš saznati{' '}
                  <Link href="/zastita-osobnih-podataka">ovdje</Link>.
                </Subtitle>
              </Column>
              <Column>
                <NewsletterForm />
              </Column>
            </ColumnWrap>
          ) : null}
          <ColumnWrap>
            <Column id="contact-us">
              <TitleWrap>
                <Title>Kontaktiraj nas</Title>
                <ContactMediums>
                  <Subtitle $flex>
                    <Icon color="silver">
                      <IconSend />
                    </Icon>
                    {contactInfo.email ? (
                      <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                    ) : (
                      <span>•••</span>
                    )}
                  </Subtitle>
                  <Subtitle $flex>
                    <Icon color="silver">
                      <IconPhoneIncoming />
                    </Icon>
                    {contactInfo.phone ? (
                      <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
                    ) : (
                      <span>•••</span>
                    )}
                  </Subtitle>
                </ContactMediums>
              </TitleWrap>
              <ContactForm />
            </Column>
            <Column>
              <PaymentCard />
            </Column>
          </ColumnWrap>
        </Section>
        <Links>
          <LinksColumn>
            <Link href="/pretplata">Pretplata</Link>
            <Link href="/edukacije">Edukacije</Link>
            <Link href="/predavaci">Predavači</Link>
          </LinksColumn>
          <LinksColumn>
            <Link href="/blog">Blog</Link>
            <Link href="/novosti">Novosti</Link>
            <Link href="/o-nama">O nama</Link>
          </LinksColumn>
          <LinksColumn>
            {pages.map((page) => (
              <Link key={page.slug} href={page.slug}>
                {page.title}
              </Link>
            ))}
          </LinksColumn>
        </Links>
        <WeBuiltThis />
        <Copyright>
          Inkubator izvrsnosti. Sva prava pridržana {new Date().getFullYear()}.
          <SocialIcons dark small />
        </Copyright>
      </InnerContainer>
    </Container>
  );
};

export { Footer };
